/* BAE Jewelry — personalization suggestions popup.
 *
 * Adds a "Need ideas?" pill beside ShineOn's personalization labels. Picking an option writes it
 * into the field.
 *
 * THE ONE THING THAT MUST NOT CHANGE: ShineOn's visible input is React-controlled and mirrors
 * into a hidden properties[_hyper[<uuid>]] input, and the HIDDEN one is what gets printed.
 * Assigning `input.value = x` updates the box the customer sees but NOT the hidden field — the
 * buyer would see their words and receive the default wording on the actual card. The native
 * setter + dispatched 'input' event below is what keeps the two in sync. Verified on the live PDP.
 *
 * Fields are matched on ShineOn's input name, which is `properties[<n>. <Label>]`. Matching on
 * the label text rather than the index keeps this working if zone order differs between listings.
 */
(function () {
  'use strict';

  var DATA_URL = window.BAE_SUGGESTIONS_URL;
  if (!DATA_URL) return;

  // label (lowercased, from the input name) -> which suggestion set to offer
  var FIELD_SETS = { 'greeting': 'greeting', 'sign-off': 'signature' };

  var data = null;
  var modal = null;
  var state = { input: null, set: null, occasion: 'Any', choice: null, opener: null };

  function parseLabel(input) {
    var m = /^properties\[\s*\d+\.\s*(.+?)\s*\]$/.exec(input.name || '');
    return m ? m[1] : null;
  }

  /* React-safe write. See the note at the top of this file before touching. */
  function applyValue(input, text) {
    var setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value').set;
    setter.call(input, text);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function optionsFor() {
    if (state.set === 'signature') return data.signature || [];
    var g = data.greeting || {};
    var base = g['Any'] || [];
    if (state.occasion === 'Any') return base;
    var pick = g[state.occasion] || [];
    return pick.concat(base.filter(function (t) { return pick.indexOf(t) === -1; }));
  }

  function buildModal() {
    var o = document.createElement('div');
    o.className = 'bae-sg-overlay';
    o.hidden = true;
    o.innerHTML =
      '<div class="bae-sg-modal" role="dialog" aria-modal="true" aria-labelledby="bae-sg-title">' +
        '<div class="bae-sg-head">' +
          '<h2 class="bae-sg-title" id="bae-sg-title">Need ideas?</h2>' +
          '<p class="bae-sg-sub"></p>' +
        '</div>' +
        '<div class="bae-sg-occasions" role="group" aria-label="Occasion"></div>' +
        '<ul class="bae-sg-list"></ul>' +
        '<div class="bae-sg-foot">' +
          '<button type="button" class="bae-sg-btn" data-act="cancel">Cancel</button>' +
          '<button type="button" class="bae-sg-btn bae-sg-btn--primary" data-act="use" disabled>Use this</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(o);

    o.addEventListener('click', function (e) {
      if (e.target === o) return close();
      var act = e.target.getAttribute && e.target.getAttribute('data-act');
      if (act === 'cancel') return close();
      if (act === 'use') {
        if (state.choice != null && state.input) applyValue(state.input, state.choice);
        return close();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !o.hidden) close();
    });
    return o;
  }

  function renderChips() {
    var wrap = modal.querySelector('.bae-sg-occasions');
    wrap.innerHTML = '';
    // Occasion only meaningfully filters the greeting; sign-offs are relationship-based.
    if (state.set !== 'greeting') { wrap.style.display = 'none'; return; }
    wrap.style.display = '';
    ['Any'].concat(data.occasions || []).forEach(function (occ) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bae-sg-chip';
      b.textContent = occ;
      b.setAttribute('aria-pressed', String(occ === state.occasion));
      b.addEventListener('click', function () {
        state.occasion = occ; state.choice = null;
        renderChips(); renderList(); syncUse();
      });
      wrap.appendChild(b);
    });
  }

  function renderList() {
    var ul = modal.querySelector('.bae-sg-list');
    ul.innerHTML = '';
    optionsFor().forEach(function (text) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bae-sg-opt';
      b.textContent = text;
      b.setAttribute('aria-pressed', String(state.choice === text));
      b.addEventListener('click', function () {
        state.choice = text;
        renderList(); syncUse();
      });
      li.appendChild(b); ul.appendChild(li);
    });
  }

  function syncUse() {
    modal.querySelector('[data-act="use"]').disabled = state.choice == null;
  }

  function open(input, set, label, opener) {
    state.input = input; state.set = set; state.choice = null;
    state.occasion = 'Any'; state.opener = opener;
    modal.querySelector('.bae-sg-sub').textContent =
      'Pick a ' + label.toLowerCase() + ', or close this and write your own.';
    renderChips(); renderList(); syncUse();
    modal.hidden = false;
    var first = modal.querySelector('.bae-sg-chip, .bae-sg-opt');
    if (first) first.focus();
  }

  function close() {
    modal.hidden = true;
    if (state.opener) state.opener.focus();
    state.input = null; state.choice = null; state.opener = null;
  }

  function decorate(input) {
    if (input.dataset.baeSg) return;
    var label = parseLabel(input);
    if (!label) return;
    var set = FIELD_SETS[label.toLowerCase()];
    if (!set) return;
    input.dataset.baeSg = '1';

    var pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'bae-sg-pill';
    pill.textContent = 'Need ideas?';
    pill.setAttribute('aria-label', 'Suggestions for ' + label);
    pill.addEventListener('click', function () { open(input, set, label, pill); });

    // Preferred placement is beside the field's label; fall back to just after the input.
    var labelEl = null, node = input;
    for (var i = 0; i < 4 && node && !labelEl; i++) {
      node = node.parentElement;
      if (!node) break;
      // Match on innerText, not textContent: ShineOn's label contains a visually-hidden
      // "Image is updating" span, so textContent reads "Sign-offImage is updating".
      labelEl = Array.prototype.find.call(node.querySelectorAll('label'), function (l) {
        return (l.innerText || l.textContent || '').trim() === label;
      });
    }
    if (labelEl) labelEl.appendChild(pill);
    else input.insertAdjacentElement('afterend', pill);
  }

  /* Read each zone's seller-set default out of the product metafield ShineOn puts on the page. */
  function zoneDefaults() {
    try {
      var pm = window.shineon.product_info.product_metafields;
      var s = pm.schema_v2;
      if (typeof s === 'string') s = JSON.parse(s);
      var map = {};
      s.zoned_fields.zones.forEach(function (z) {
        map[z.props.overrides.label] = z.props.value;
      });
      return map;
    } catch (e) { return {}; }
  }

  /* WHY THIS EXISTS — do not remove.
   * ShineOn's live preview renders an EMPTY field as its LABEL. So a buyer who edits only the
   * greeting sees a card reading "Their name" and "Sign-off" where the other two should be, and
   * the cart carries empty strings for them. Seeding every field with its seller-set default
   * means no field is ever empty: the preview always shows real wording, and the order always
   * carries explicit values instead of relying on how ShineOn treats a blank. It also matches the
   * intent of the defaults — the card should read correctly for a buyer who changes nothing. */
  function prefill(inputs) {
    var defaults = zoneDefaults();
    Array.prototype.forEach.call(inputs, function (input) {
      if (input.dataset.baePrefilled) return;
      var label = parseLabel(input);
      if (!label) return;
      input.dataset.baePrefilled = '1';
      var d = defaults[label];
      if (d && !input.value) applyValue(input, d);
    });
  }

  function scan() {
    var inputs = document.querySelectorAll('.so-hyper-fields input[type="text"]');
    if (!inputs.length) return false;
    Array.prototype.forEach.call(inputs, decorate);
    prefill(inputs);
    return true;
  }

  fetch(DATA_URL)
    .then(function (r) { return r.json(); })
    .then(function (json) {
      data = json;
      modal = buildModal();
      if (!scan()) {
        // ShineOn renders its fields asynchronously; wait for them.
        var obs = new MutationObserver(function () { if (scan()) obs.disconnect(); });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () { obs.disconnect(); }, 15000);
      }
    })
    .catch(function () { /* suggestions are an enhancement — never block the form */ });
})();
